/**
 * API client utility that wraps fetch and handles:
 * - 401 Unauthorized (token expiration) - automatically refreshes token and retries
 * - 403 Forbidden responses - Shows a modal
 */

let onForbiddenCallback = null;
let getTokenFunction = null;
let refreshTokenFunction = null;

/**
 * Sets the callback function to be called when a 403 Forbidden response is received
 * @param {Function} callback - Function to call when 403 is detected
 */
export const setForbiddenHandler = (callback) => {
    onForbiddenCallback = callback;
};

/**
 * Sets the token getter and refresh functions for automatic token refresh
 * @param {Function} getToken - Function that returns current token (async)
 * @param {Function} refreshToken - Function that refreshes the token (async)
 */
export const setTokenFunctions = (getToken, refreshToken) => {
    getTokenFunction = getToken;
    refreshTokenFunction = refreshToken;
};

/**
 * Checks if error response indicates token expiration
 */
const isTokenExpiredError = async (response) => {
    if (response.status !== 401) {
        return false;
    }

    try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            // Clone response to read body without consuming it
            const clonedResponse = response.clone();
            const errorData = await clonedResponse.json().catch(() => ({}));
            const errorMessage = errorData.message || errorData.error || '';
            
            // Check if error message indicates token expiration
            return errorMessage.toLowerCase().includes('token') && 
                   (errorMessage.toLowerCase().includes('expired') || 
                    errorMessage.toLowerCase().includes('refresh'));
        }
    } catch {
        // If we can't parse the response, assume it's a token expiration for 401
        return true;
    }

    return true; // Default to true for 401 Unauthorized
};

/**
 * Wrapper around fetch that intercepts 401/403 responses and handles token refresh
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options (same as native fetch)
 * @param {number} retryCount - Internal parameter to track retry attempts
 * @returns {Promise<Response>} - The fetch response
 */
export const apiFetch = async (url, options = {}, retryCount = 0) => {
    const MAX_RETRIES = 1; // Only retry once to avoid infinite loops
    
    // Ensure headers object exists
    if (!options.headers) {
        options.headers = {};
    }
    
    // Add token if we have a token getter function and no Authorization header
    const hasAuthHeader = options.headers['Authorization'] || options.headers['authorization'];
    if (getTokenFunction && !hasAuthHeader) {
        try {
            const token = await getTokenFunction(false);
            if (token) {
                options.headers['Authorization'] = `Bearer ${token}`;
            }
        } catch (error) {
            console.warn('Failed to get token for API request:', error);
        }
    }

    let response = await fetch(url, options);
    
    // Check for 401 Unauthorized - might be token expiration
    if (response.status === 401 && retryCount < MAX_RETRIES && refreshTokenFunction && getTokenFunction) {
        const isExpired = await isTokenExpiredError(response);
        
        if (isExpired) {
            console.log('Token expired (15 minutes), attempting to refresh...');
            
            try {
                // Refresh the token
                const refreshed = await refreshTokenFunction();
                
                if (refreshed) {
                    // Get the new token
                    const newToken = await getTokenFunction(true); // Force refresh
                    
                    if (newToken) {
                        // Clone options and update Authorization header
                        const newOptions = {
                            ...options,
                            headers: {
                                ...options.headers,
                                'Authorization': `Bearer ${newToken}`
                            }
                        };
                        
                        console.log('Token refreshed successfully, retrying request...');
                        
                        // Retry the request with new token (increment retry count)
                        response = await apiFetch(url, newOptions, retryCount + 1);
                    } else {
                        console.warn('Token refresh succeeded but no token was returned');
                    }
                } else {
                    console.warn('Token refresh failed or returned false');
                }
            } catch (refreshError) {
                console.error('Failed to refresh token:', refreshError);
                // Return original response if refresh fails
            }
        }
    }
    
    // Check for 403 Forbidden response
    if (response.status === 403) {
        if (onForbiddenCallback) {
            onForbiddenCallback();
        }
    }
    
    return response;
};

